import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Lock, Upload, Plus, Loader2, FileText, Shield, AlertTriangle, Clock, History, X } from "lucide-react";
import { toast } from "sonner";

const CONF_COLOR = {
  public: "#22C55E",
  internal: "#3B82F6",
  restricted: "#F59E0B",
  secret: "#EF4444",
};

const STATUS_COLOR = {
  draft: "#94A3B8",
  active: "#22C55E",
  expired: "#EF4444",
  terminated: "#64748B",
  archived: "#94A3B8",
};

const fmtGhs = (n) => (n == null ? "—" : `GH₵ ${n.toLocaleString()}`);

export default function Vault() {
  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [versions, setVersions] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");

  const blank = { title: "", type: "nda", confidentiality: "internal", status: "draft", expiry_date: "", description: "", content: "", parties: [] };
  const [newDoc, setNewDoc] = useState(blank);

  const load = () => {
    setLoading(true);
    const params = [];
    if (filterType) params.push(`type=${filterType}`);
    if (filterStatus) params.push(`status=${filterStatus}`);
    if (search) params.push(`q=${encodeURIComponent(search)}`);
    const url = `/vault/documents${params.length ? "?" + params.join("&") : ""}`;
    Promise.all([
      api.get(url),
      api.get("/vault/types"),
      api.get("/vault/stats"),
      api.get("/vault/expiring?days=90"),
    ]).then(([d, t, s, e]) => {
      setDocs(d.data);
      setTypes(t.data);
      setStats(s.data);
      setExpiring(e.data);
    }).finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [filterType, filterStatus]);

  const handleSearch = (e) => { if (e.key === "Enter") load(); };

  const save = async () => {
    if (!newDoc.title.trim()) return;
    try {
      await api.post("/vault/documents", newDoc);
      setNewDoc(blank);
      setCreating(false);
      load();
      toast.success("Document filed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const openDoc = async (d) => {
    try {
      const [full, vs] = await Promise.all([
        api.get(`/vault/documents/${d.id}`),
        api.get(`/vault/documents/${d.id}/versions`),
      ]);
      setSelected(full.data);
      setVersions(vs.data);
    } catch { toast.error("Failed to open"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this document and all its versions?")) return;
    try {
      await api.delete(`/vault/documents/${id}`);
      setSelected(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const downloadFile = async (d) => {
    try {
      const res = await api.get(`/vault/documents/${d.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: d.file_mime || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = d.file_name || `${d.title}.bin`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="vault-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Lock className="w-3.5 h-3.5" style={{ color: "var(--gold-400)" }} strokeWidth={1.6} />
            <span className="label-eyebrow">// Secure Institutional Vault</span>
          </div>
          <h1 className="font-display text-3xl text-white tracking-tight">Document Vault</h1>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">
            Contracts, board resolutions, MOUs, policies — versioned, tagged, expiry-tracked, audit-logged.
          </p>
        </div>
        <button onClick={() => setCreating(!creating)} data-testid="vault-new-btn" className="text-[11px] font-mono tracking-widest px-3 py-2 flex items-center gap-1.5" style={{ background: "var(--gold-500)", color: "#050B14" }}>
          <Plus className="w-3 h-3" /> FILE DOCUMENT
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="vault-stats">
          <Stat eyebrow="01 // TOTAL" value={stats.total} label="Documents under management" />
          <Stat eyebrow="02 // ACTIVE" value={stats.by_status?.active || 0} label="Active contracts" color="#22C55E" />
          <Stat eyebrow="03 // 30d" value={stats.expiring_30d} label="Expiring in 30 days" color={stats.expiring_30d > 0 ? "#F59E0B" : "#F8FAFC"} />
          <Stat eyebrow="04 // 90d" value={stats.expiring_90d} label="Expiring in 90 days" color={stats.expiring_90d > 0 ? "#EF4444" : "#F8FAFC"} />
        </div>
      )}

      {/* Expiring alert */}
      {expiring.length > 0 && (
        <div className="nexus-glass-panel p-4 border border-amber-500/30" data-testid="expiring-alert">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
            <span className="label-eyebrow text-amber-300">// Expiring within 90 days</span>
          </div>
          <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
            {expiring.slice(0, 6).map((d) => (
              <button key={d.id} onClick={() => openDoc(d)} className="w-full text-left flex items-center gap-3 p-2 border border-white/5 hover:border-white/20 transition-all">
                <Clock className="w-3 h-3 shrink-0" style={{ color: d.days_to_expiry <= 30 ? "#EF4444" : "#F59E0B" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{d.title}</div>
                  <div className="text-[11px] text-slate-500">{d.type_label}</div>
                </div>
                <span className="font-mono text-[11px]" style={{ color: d.days_to_expiry <= 30 ? "#EF4444" : "#F59E0B" }}>
                  {d.days_to_expiry < 0 ? "EXPIRED" : `${d.days_to_expiry}d`}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {creating && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="nexus-glass-panel p-5 space-y-3" data-testid="vault-create-form">
          <div className="label-eyebrow">// New document</div>
          <input value={newDoc.title} onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })} placeholder="Document title" data-testid="vault-title" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select value={newDoc.type} onChange={(e) => setNewDoc({ ...newDoc, type: e.target.value })} data-testid="vault-type" className="bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
              {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <select value={newDoc.confidentiality} onChange={(e) => setNewDoc({ ...newDoc, confidentiality: e.target.value })} className="bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none">
              <option value="public">Public</option><option value="internal">Internal</option><option value="restricted">Restricted</option><option value="secret">Secret</option>
            </select>
            <input type="date" value={newDoc.expiry_date?.split("T")[0] || ""} onChange={(e) => setNewDoc({ ...newDoc, expiry_date: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="bg-[#0A111F] border border-white/10 text-white text-sm px-3 py-2 focus:border-[#D4AF37]/60 focus:outline-none" />
          </div>
          <textarea value={newDoc.description} onChange={(e) => setNewDoc({ ...newDoc, description: e.target.value })} rows={2} placeholder="Short description" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm p-2.5 resize-none focus:border-[#D4AF37]/60 focus:outline-none" />
          <textarea value={newDoc.content} onChange={(e) => setNewDoc({ ...newDoc, content: e.target.value })} rows={6} placeholder="Document content (or upload file)" className="w-full bg-[#0A111F] border border-white/10 text-white text-sm p-2.5 resize-none font-mono focus:border-[#D4AF37]/60 focus:outline-none" />
          <button onClick={save} data-testid="vault-save" className="text-[11px] font-mono tracking-widest px-3 py-2" style={{ background: "var(--gold-500)", color: "#050B14" }}>FILE DOCUMENT</button>
        </motion.div>
      )}

      {/* Filter strip */}
      <div className="flex flex-wrap gap-2 items-center" data-testid="vault-filters">
        <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={handleSearch} placeholder="Search title / tags…" className="bg-[#0A111F] border border-white/10 text-white text-xs px-3 py-1.5 w-60 focus:border-[#D4AF37]/60 focus:outline-none" data-testid="vault-search" />
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="bg-[#0A111F] border border-white/10 text-white text-xs px-2 py-1.5 focus:border-[#D4AF37]/60 focus:outline-none">
          <option value="">All types</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="bg-[#0A111F] border border-white/10 text-white text-xs px-2 py-1.5 focus:border-[#D4AF37]/60 focus:outline-none">
          <option value="">All status</option>
          <option value="draft">Draft</option><option value="active">Active</option><option value="expired">Expired</option><option value="terminated">Terminated</option><option value="archived">Archived</option>
        </select>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          {loading ? (
            <div className="p-10 text-slate-400 font-mono text-xs flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> LOADING VAULT…</div>
          ) : docs.length === 0 ? (
            <div className="nexus-glass-panel p-10 text-center" data-testid="vault-empty">
              <Shield className="w-8 h-8 mx-auto text-slate-600 mb-3" strokeWidth={1.5} />
              <h3 className="font-display text-lg text-white">No documents on file</h3>
              <p className="text-sm text-slate-400 mt-1">File your first contract, MOU, or board resolution to start the vault.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="vault-list">
              {docs.map((d) => (
                <button key={d.id} onClick={() => openDoc(d)} data-testid={`vault-${d.id}`} className={`w-full text-left flex items-center gap-3 p-3 nexus-card hover:border-[#D4AF37]/30 ${selected?.id === d.id ? "border-[#D4AF37]/60 bg-[#D4AF37]/5" : ""}`}>
                  <FileText className="w-3.5 h-3.5 shrink-0" style={{ color: CONF_COLOR[d.confidentiality] || "#94A3B8" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{d.title}</div>
                    <div className="text-[11px] text-slate-500 truncate">{d.type_label} · v{d.current_version}{d.description ? " · " + d.description : ""}</div>
                  </div>
                  {d.expiry_date && <span className="font-mono text-[10px] text-slate-500">{new Date(d.expiry_date).toLocaleDateString()}</span>}
                  <span className="font-mono text-[9px] tracking-widest" style={{ color: STATUS_COLOR[d.status] }}>{d.status?.toUpperCase()}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div className="lg:col-span-1">
          {selected ? (
            <div className="nexus-glass-panel p-5 space-y-4 sticky top-6" data-testid="vault-detail">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="label-eyebrow" style={{ color: CONF_COLOR[selected.confidentiality] }}>{selected.confidentiality?.toUpperCase()}</div>
                  <h2 className="font-display text-lg text-white mt-1 leading-tight">{selected.title}</h2>
                  <div className="text-xs text-slate-400 mt-0.5">{selected.type_label} · v{selected.current_version}</div>
                </div>
                <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-white p-1"><X className="w-3.5 h-3.5" /></button>
              </div>
              {selected.description && <p className="text-sm text-slate-200 leading-relaxed">{selected.description}</p>}
              <div className="grid grid-cols-2 gap-2 text-[12px]">
                {selected.effective_date && <KV k="Effective" v={new Date(selected.effective_date).toLocaleDateString()} />}
                {selected.expiry_date && <KV k="Expires" v={new Date(selected.expiry_date).toLocaleDateString()} />}
                {selected.value_ghs && <KV k="Value" v={fmtGhs(selected.value_ghs)} />}
                <KV k="Status" v={selected.status} />
              </div>
              {selected.parties?.length > 0 && (
                <div>
                  <div className="label-eyebrow mb-1">// Parties</div>
                  <ul className="space-y-1">
                    {selected.parties.map((p, i) => (
                      <li key={i} className="text-[12px] text-slate-300">{p.name}{p.role ? ` — ${p.role}` : ""}</li>
                    ))}
                  </ul>
                </div>
              )}
              {versions.length > 0 && (
                <div className="pt-3 border-t border-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <History className="w-3 h-3 text-slate-400" />
                    <span className="label-eyebrow">// Version History</span>
                  </div>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {versions.map((v) => (
                      <div key={v.id} className="flex items-center gap-2 text-[11px]">
                        <span className="font-mono text-[#D4AF37]">v{v.version_number}</span>
                        <span className="text-slate-400 truncate flex-1">{v.change_summary}</span>
                        <span className="text-slate-600 font-mono text-[10px]">{new Date(v.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={() => remove(selected.id)} data-testid="vault-delete" className="w-full text-[11px] font-mono tracking-widest py-2 border border-white/10 hover:border-red-500/40 hover:text-red-400 text-slate-400">DELETE DOCUMENT</button>
              {selected.file_name && (
                <button onClick={() => downloadFile(selected)} data-testid="vault-download" className="w-full text-[11px] font-mono tracking-widest py-2 border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 text-[#D4AF37] mt-2">
                  DOWNLOAD ORIGINAL · {selected.file_name}
                </button>
              )}
            </div>
          ) : (
            <div className="nexus-glass-panel p-6 text-center">
              <FileText className="w-8 h-8 mx-auto text-slate-600 mb-2" strokeWidth={1.5} />
              <p className="text-sm text-slate-400">Select a document to view details, parties, and version history.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ eyebrow, value, label, color }) => (
  <div className="nexus-card p-4">
    <div className="label-eyebrow">{eyebrow}</div>
    <div className="font-display text-2xl mt-1" style={{ color: color || "#F8FAFC" }}>{value}</div>
    <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
  </div>
);

const KV = ({ k, v }) => (
  <div className="flex items-center justify-between gap-2 py-1 border-b border-white/5">
    <span className="label-eyebrow">{k}</span>
    <span className="text-white text-[12px]">{v}</span>
  </div>
);
